
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import TableFilterable from '@/components/reports-v3/components/filterables/TableFilterable.vue';
import FilterWidget from '@/components/reports-v3/components/codedWidgets/shared/FilterWidget.vue';
import { PermissionsGroup } from '@/store/models.def';
import moment from 'moment';
import gdbx from '@/store/modules/gdbx';
import { roundToFixed } from '@/util/number';
import Checkbox from 'primevue/checkbox';
import { getTableName } from '@/worker/db/sqlHelpers';

@Component({
	components: { TableFilterable, Checkbox },
})
export default class RecommendationReorderAnalysisTable extends FilterWidget {
	public get permissionIds(): PermissionsGroup[] {
		return ['stocks'];
	}

	public isIncludeInactive: boolean = false;
	public isOnlyFilledNewReorder: boolean = false;
	public isShowDetails: boolean = false;

	public filterIds: Array<
		| 'date'
		| 'dateAsOf'
		| 'dateRange'
		| 'stockItems'
		| 'agents'
		| 'customers'
		| 'suppliers'
	> = [];

	public tableItems: any[] = [];
	public tableFields: any[] = [];

	public get exportFileName() {
		const formattedDate = moment(this.selectedAsOfDate).format('DD MMM YY');

		return 'Reorder Analysis(System)' + '_' + 'As Of ' + formattedDate;
	}

	public get dateFormatted() {
		return [this.selectedAsOfDate];
	}

	public get numDecimal() {
		return gdbx.numDecimal;
	}

	public get updatedTableFields() {
		const tableFieldClone = [...this.tableFields];

		const warehousePosition = tableFieldClone.findIndex(
			(field) => field.key === 'warehouse_qty',
		);

		if (!this.isShowDetails) {
			tableFieldClone.splice(warehousePosition, 4);
		}

		return tableFieldClone;
	}

	public get updatedTableItems() {
		const newItems: any[] = [];

		if (this.isIncludeInactive && this.isOnlyFilledNewReorder) {
			this.tableItems.filter((item) => {
				if (
					item.hasOwnProperty('order_decision') &&
					(item.order_decision || item.order_decision === 0)
				) {
					newItems.push(item);
				}
			});
			return newItems.length > 0 ? newItems : this.tableItems;
		} else if (this.isIncludeInactive && !this.isOnlyFilledNewReorder) {
			return this.tableItems;
		} else if (!this.isIncludeInactive && this.isOnlyFilledNewReorder) {
			this.tableItems
				.filter((item) => item.isActive === 'True')
				.filter(
					(item) => item.isDiscontinued !== 'True' || item.isDiscontinued === null,
				)
				.filter((item) => {
					if (
						item.hasOwnProperty('order_decision') &&
						(item.order_decision || item.order_decision === 0)
					) {
						newItems.push(item);
					}
				});

			return newItems.length > 0
				? newItems
				: this.tableItems
						.filter((item) => item.isActive === 'True')
						.filter(
							(item) =>
								item.isDiscontinued !== 'True' || item.isDiscontinued === null,
						);
		} else if (!this.isIncludeInactive && !this.isOnlyFilledNewReorder) {
			return this.tableItems
				.filter((item) => item.isActive === 'True')
				.filter(
					(item) => item.isDiscontinued !== 'True' || item.isDiscontinued === null,
				);
		}
	}

	public get expensiveHook() {
		const { selectedAsOfDate, selectedDateRange, selectedStockItems } = this;
		return JSON.stringify([
			selectedAsOfDate,
			selectedDateRange,
			selectedStockItems,
		]);
	}

	public async expensiveCalc() {
		const jobId = this.currentJobId;
		this.generateLoadingText(0.2, 0.3);

		const msPerDay = 24 * 60 * 60 * 1000;
		const asOfDate = this.selectedAsOfDate;

		// Build manual filter clauses
		const escapeStr = (s: string) => s.replace(/'/g, '\'\'');
		let stockItemFilter = '';
		let stockItemCodeFilter = '';
		if (this.selectedStockItems.length > 0) {
			const list = this.selectedStockItems.map((s) => `'${escapeStr(s)}'`).join(',');
			stockItemFilter = ` AND "ITEMCODE" IN (${list})`;
			stockItemCodeFilter = ` AND "CODE" IN (${list})`;
		}

		const stockTransTable = getTableName('diyStockTrans');
		const salesOrderTable = getTableName('salesOrder');
		const purchaseOrderTable = getTableName('purchaseOrder');
		const purchaseRequestTable = getTableName('purchaseRequest');
		const globalStockTable = getTableName('diyGlobalStocks');

		const queryHead = await this._ensureTablesLoaded([
			'diyStockTrans', 'salesOrder', 'purchaseOrder', 'purchaseRequest', 'diyGlobalStocks',
		] as any[]);
		this.checkInterrupted(jobId);
		this.generateLoadingText(0.3, 0.9);

		// Fetch data in parallel using rawQuery
		const [
			asOfStockTransData,
			salesOrderData,
			purchaseOrderData,
			purchaseRequestData,
			globalStockData,
		] = await Promise.all([
			// Stock Trans (asOf): get itemCode, date, qty, qtyOut for period sales calc
			queryHead.rawQuery(`
				SELECT "ITEMCODE" as itemCode,
					TRY_CAST("POSTDATE" AS BIGINT) as date,
					TRY_CAST("QTY" AS DOUBLE) as qty,
					TRY_CAST("QTYOUT" AS DOUBLE) as qtyOut
				FROM ${stockTransTable}
				WHERE TRY_CAST("POSTDATE" AS BIGINT) <= ${asOfDate}
					${stockItemFilter}
			`),
			// Sales Order: SUM outstanding quantity by item
			queryHead.rawQuery(`
				SELECT "ITEMCODE" as itemCode,
					SUM(TRY_CAST("OSQTY" AS DOUBLE)) as osQty
				FROM ${salesOrderTable}
				WHERE TRY_CAST("DOCDATE" AS BIGINT) <= ${asOfDate}
					${stockItemFilter}
				GROUP BY "ITEMCODE"
			`),
			// Purchase Order: SUM outstanding quantity by item
			queryHead.rawQuery(`
				SELECT "ITEMCODE" as itemCode,
					SUM(TRY_CAST("OSQTY" AS DOUBLE)) as osQty
				FROM ${purchaseOrderTable}
				WHERE TRY_CAST("DOCDATE" AS BIGINT) <= ${asOfDate}
					${stockItemFilter}
				GROUP BY "ITEMCODE"
			`),
			// Purchase Request: SUM outstanding quantity by item
			queryHead.rawQuery(`
				SELECT "ITEMCODE" as itemCode,
					SUM(TRY_CAST("OSQTY" AS DOUBLE)) as osQty
				FROM ${purchaseRequestTable}
				WHERE TRY_CAST("DOCDATE" AS BIGINT) <= ${asOfDate}
					${stockItemFilter}
				GROUP BY "ITEMCODE"
			`),
			// Global Stock: get lead time, isActive, isDiscontinued
			queryHead.rawQuery(`
				SELECT "CODE" as itemCode,
					TRY_CAST("LEADTIME" AS DOUBLE) as leadTime,
					"ISACTIVE" as isActive,
					"UDF_DISCON" as isDiscontinued
				FROM ${globalStockTable}
				WHERE 1=1${stockItemCodeFilter}
			`),
		]);
		this.checkInterrupted(jobId);

		// Build lookup maps
		const salesOrderMap: Map<string, number> = new Map();
		for (const row of salesOrderData) {
			salesOrderMap.set(row.itemCode, Number(row.osQty) || 0);
		}

		const purchaseOrderMap: Map<string, number> = new Map();
		for (const row of purchaseOrderData) {
			purchaseOrderMap.set(row.itemCode, Number(row.osQty) || 0);
		}

		const purchaseRequestMap: Map<string, number> = new Map();
		for (const row of purchaseRequestData) {
			purchaseRequestMap.set(row.itemCode, Number(row.osQty) || 0);
		}

		interface GlobalStockInfo {
			leadTime: number;
			isActive: string;
			isDiscontinued: string | null;
		}
		const globalStockMap: Map<string, GlobalStockInfo> = new Map();
		for (const row of globalStockData) {
			globalStockMap.set(row.itemCode, {
				leadTime: Number(row.leadTime) || 0,
				isActive: row.isActive || '',
				isDiscontinued: row.isDiscontinued || null,
			});
		}

		// Group stockTrans by itemCode for quantity and periodSales calculation
		interface StockTransItem {
			date: number;
			qty: number;
			qtyOut: number;
		}
		const stockTransByItem: Map<string, StockTransItem[]> = new Map();
		for (const row of asOfStockTransData) {
			const itemCode = row.itemCode as string;
			if (!stockTransByItem.has(itemCode)) {
				stockTransByItem.set(itemCode, []);
			}
			stockTransByItem.get(itemCode)!.push({
				date: Number(row.date) || 0,
				qty: Number(row.qty) || 0,
				qtyOut: Number(row.qtyOut) || 0,
			});
		}

		this.generateLoadingText(0.95);

		// Build result
		const result: Array<{
			item_desc: string;
			warehouse_qty: number;
			shipping_qty: number;
			so_qty: number;
			ordered_qty: number;
			actual_qty: number;
			period_sales: number;
			daily_demand: number;
			inventory_days: number;
			lead_time_system: number;
			recommend_reorder_qty: number;
			order_decision: number | null;
			isActive: string;
			isDiscontinued: string | null;
		}> = [];

		for (const [itemCode, stockTransItems] of stockTransByItem) {
			// Must have global stock data
			if (!globalStockMap.has(itemCode)) {
				continue;
			}

			const globalInfo = globalStockMap.get(itemCode)!;
			const leadTime = globalInfo.leadTime;

			// Calculate quantity (sum of qty)
			const quantity = stockTransItems.reduce((a, b) => a + b.qty, 0);

			// Calculate periodSales: filter records where days diff < leadTime
			const periodSales = stockTransItems
				.filter((item) => {
					const days = Math.floor((asOfDate - item.date) / msPerDay);
					return days < leadTime;
				})
				.reduce((a, b) => a + Math.abs(b.qtyOut), 0);

			const shippingQuantity = purchaseOrderMap.get(itemCode) || 0;
			const soQuantity = salesOrderMap.get(itemCode) || 0;
			const orderedQuantity = purchaseRequestMap.get(itemCode) || 0;

			const dailyDemand = leadTime !== 0
				? roundToFixed(periodSales / leadTime, this.numDecimal)
				: 0;

			const inventoryDays = dailyDemand > 0
				? Math.trunc(quantity / dailyDemand)
				: 0;

			const actualQty = shippingQuantity + quantity + orderedQuantity - soQuantity;

			const reorderLevel = Math.round(leadTime * dailyDemand * 100) / 100;
			const recommendOrder = Math.round((reorderLevel - actualQty) * 100) / 100;

			result.push({
				item_desc: gdbx.stockItemNames[itemCode],
				warehouse_qty: quantity,
				shipping_qty: shippingQuantity,
				so_qty: soQuantity,
				ordered_qty: orderedQuantity,
				actual_qty: actualQty,
				period_sales: periodSales,
				daily_demand: dailyDemand,
				inventory_days: inventoryDays,
				lead_time_system: leadTime,
				recommend_reorder_qty: recommendOrder,
				order_decision: null,
				isActive: globalInfo.isActive,
				isDiscontinued: globalInfo.isDiscontinued,
			});
		}

		this.tableItems = result;

		this.tableFields = [
			{
				key: 'item_desc',
				sortable: true,
				tdClass: (value) => {
					if (value) {
						return 'custom-style';
					}
				},
				stickyColumn: true,
			},
			{
				key: 'warehouse_qty',
				label: 'Warehouse Qty',
				sortable: true,
				variant: 'info',
				class: 'text-center',
			},
			{
				key: 'shipping_qty',
				label: 'Shipping',
				sortable: true,
				variant: 'info',
				class: 'text-center',
			},
			{
				key: 'so_qty',
				label: 'SO Qty',
				sortable: true,
				variant: 'info',
				class: 'text-center',
			},
			{
				key: 'ordered_qty',
				label: 'Ordered',
				sortable: true,
				variant: 'info',
				class: 'text-center',
			},
			{
				key: 'actual_qty',
				label: 'Actual Qty',
				sortable: true,
				class: 'text-center',
			},
			{
				key: 'lead_time_system',
				label: 'Lead Time',
				sortable: true,
				class: 'text-center',
			},
			{
				key: 'period_sales',
				sortable: true,
				class: 'text-center',
			},
			{
				key: 'daily_demand',
				sortable: true,
				class: 'text-center',
			},
			// {
			// 	key: 'reorder_level',
			// 	sortable: true,
			// 	sortByFormatted: (value, key, item) => {
			// 		const reorderLevel = item.lead_time_system * item.daily_demand;
			// 		return Math.round(reorderLevel * 100) / 100;
			// 	},
			// },
			{
				key: 'inventory_days',
				label: 'Inventory Days',
				sortable: true,
				// sortByFormatted: (value, key, item) => {
				// 	const sum =
				// 		item.daily_demand > 0 ? item.warehouse_qty / item.daily_demand : 0;
				// 	return Math.trunc(sum);
				// },
				class: 'text-center',
			},
			{
				key: 'recommend_reorder_qty',
				label: 'Recommend Order Qty',
				sortable: true,
				// sortByFormatted: (value, key, item) => {
				// 	const reorderLevel = item.lead_time_system * item.daily_demand;
				// 	return (
				// 		Math.round(
				// 			(Math.round(reorderLevel * 100) / 100 - item.warehouse_qty) * 100,
				// 		) / 100
				// 	);
				// },
				class: 'text-center',
			},
			{
				key: 'order_decision',
				label: 'Order Decision',
				sortable: false,
				class: 'text-center',
			},
		];

		this.generateLoadingText(1);
		this.saveHistory('tableItems', 'tableFields');
	}

	public onHovering(value: boolean, filterId: string) {
		this.$emit('hovering', value, filterId);
	}
	public onEditing(value: boolean, filterId: string) {
		this.$emit('editing', value, filterId);
	}
}
